/**
 * Serviço utilitário para preservar e restaurar itens específicos do localStorage
 * durante operações de limpeza.
 */

import { APP_UPDATE_PENDING_KEY } from '../../constants/app-update.constants';

export interface PreservedItem {
  key: string;
  value: string;
}

/**
 * Prefixos das chaves do localStorage que devem ser preservadas
 * durante operações de limpeza.
 */
const PRESERVED_KEY_PREFIXES = [
  'ls.comunicado.id_',
  'ls.returnUrl',
  APP_UPDATE_PENDING_KEY
];

/**
 * Verifica se uma chave deve ser preservada com base nos prefixos configurados.
 *
 * @param key - Chave do localStorage a ser verificada
 * @returns true se a chave corresponder a algum prefixo configurado
 */
function shouldPreserveKey(key: string): boolean {
  return PRESERVED_KEY_PREFIXES.some(prefix => key.startsWith(prefix));
}

/**
 * Obtém um item do localStorage de forma segura.
 *
 * @param key - Chave do item no localStorage
 * @returns Objeto PreservedItem se o valor existir, ou null caso contrário
 */
function getStorageItem(key: string): PreservedItem | null {
  const value = localStorage.getItem(key);
  return value !== null ? { key, value } : null;
}

/**
 * Coleta todas as chaves do localStorage em um array.
 *
 * @returns Array com todas as chaves presentes no localStorage
 */
function getAllStorageKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key !== null) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * Preserva itens do localStorage que correspondem aos prefixos configurados.
 *
 * @returns Array de objetos contendo as chaves e valores preservados
 */
export function preserveItems(): PreservedItem[] {
  return getAllStorageKeys()
    .filter(shouldPreserveKey)
    .map(getStorageItem)
    .filter((item): item is PreservedItem => item !== null);
}

/**
 * Restaura os itens preservados de volta ao localStorage.
 *
 * @param preservedItems - Array de objetos contendo as chaves e valores a serem restaurados
 */
export function restorePreserved(preservedItems: PreservedItem[]): void {
  preservedItems.forEach(item => {
    localStorage.setItem(item.key, item.value);
  });
}
